/**
 * config.ts – Single Source für die App-Konfiguration.
 *
 * Hier stehen Titel (appName), Logo (logoUrl, logoColor),
 * Breakpoints und Growl-Defaults. Alle Konsumenten importieren appConfig von hier.
 *
 * Sidebar-Navigation, SidebarFooter, UserMenu und QuickActions kommen vom Server
 * (Class_Navigation → NavigationContext). config.ts enthält nur noch Layout-Konfiguration.
 *
 * Zusammenhänge:
 *   - types.ts              → AppConfig, Breakpoints, GrowlConfig
 *   - App.tsx               → Layout, Logo, Accordion, Favicon-Setup
 *   - LoginPage.tsx         → Logo und Titel auf der Login-Seite
 *   - Sidebar.tsx           → Logo, Breakpoints
 *   - Growl.tsx             → Growl-Konfiguration (Position, Auto-Dismiss)
 *   - NavigationContext.tsx  → Sidebar + Menüs kommen vom Server (nicht mehr hier)
 *
 * appName ändern? Hier anpassen und zusätzlich <title> in index.html setzen (Tab-Titel vor App-Load).
 */
import type { AppConfig, Breakpoints, GrowlConfig } from '@/types';

/** Standard-Breakpoints (px). Schwellen für responsive Layouts: mobile < tablet < desktop < wide. */
export const defaultBreakpoints: Breakpoints = {
  tablet: 768,
  desktop: 1024,
  wide: 1280,
  paginationTextFrom: 768,
  tableStickyLeftFrom: 768,
  viewportRightSideRatio: 0.7,
};

/** Standard-Growl/Toast-Konfiguration: Position, Breite, Auto-Dismiss-Dauer und Abstand. */
export const defaultGrowlConfig: GrowlConfig = {
  top: 56,
  right: 16,
  maxWidth: 384,
  autoDismissMs: 4000,
  gap: 8,
};

/** Vollständige App-Standardkonfiguration. Single Source für Branding und Struktur. */
export const defaultConfig: AppConfig = {
  initialSidebarMode: 'expanded',
  defaultSidebarWidth: 256,
  maxSidebarWidth: 400,
  topNavHeight: 64,
  growl: { ...defaultGrowlConfig },
  breakpoints: { ...defaultBreakpoints },
  sidebarAccordion: true,
  sidebarName: 'Xeno',
  appName: 'Xeno Admin',
  logoUrl: './images/logo.svg',
  logoColor: '#2563eb',
  // sidebar + sidebarFooter kommen vom Server (Class_Navigation → NavigationContext).
  // Keine statische Definition mehr nötig – Single Source of Truth in PHP.
};

/** Von allen Konsumenten zu verwendende App-Konfiguration (Single Source: diese Datei). */
export const appConfig: AppConfig = defaultConfig;

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Gender + Organisation – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_customers.gender / system_dates_signupers.gender TINYINT).
// Werden per deriveColorSet() in Rahmen-, Hintergrund-, Text- und Badge-Farben abgeleitet.
//
// Spaeter (Phase 2) koennen diese Werte durch eine DB-Entitaet (system_contacttypes)
// ueberschrieben werden. resolveGenderConfig() akzeptiert dafuer einen optionalen
// configMap-Parameter; ohne configMap greifen diese Fallbacks.
//
// Verwendet von: ItemChip (Rahmen, Badges, SyncBar), StatsBar, DateHeader,
// BookingCounter, GanttView, GuiElements (Demo-Karten).
// ═══════════════════════════════════════════════════════════════════════════════

import type { SpecialTypeConfig } from '@/types';

/**
 * Fallback-Config pro Gender-DB-Wert (0=Maennlich, 1=Weiblich, 2=Divers).
 * Literal-Zahlen als Keys – kein Import von Gender-Enum noetig, kein Import-Zyklus.
 */
export const GENDER_DEFAULTS: Record<number, SpecialTypeConfig> = {
  0: { label: 'Männlich', color: '#3b82f6', icon: 'mars' },
  1: { label: 'Weiblich', color: '#ec4899', icon: 'venus' },
  2: { label: 'Divers',   color: '#a855f7', icon: 'non-binary' },
};

/** Neutraler Fallback fuer null/unbekanntes Gender (circle-dot als stilistisch neutrales Symbol). */
export const GENDER_DEFAULT_FALLBACK: SpecialTypeConfig = { label: 'Unbekannt', color: '#94a3b8', icon: 'circle-dot' };

/** Visuelle Identitaet fuer Organisations-Kontakte (system_customers.type=1). */
export const ORGANISATION_DEFAULT: SpecialTypeConfig = { label: 'Organisation', color: '#f59e0b', icon: 'building' };

// ═══════════════════════════════════════════════════════════════════════════════
// Zimmerkategorie-Modus (DEHOGA vs. Vereinfacht)
// ═══════════════════════════════════════════════════════════════════════════════
//
// Zimmerkategorien (EZ/DZ/MBZ) werden nach DEHOGA-Standard aus der vorgesehenen
// Belegung (maxPersons) abgeleitet, NICHT aus der Bettenart:
//   - EZ = maxPersons 1 (egal ob Einzel- oder Doppelbett)
//   - DZ = maxPersons 2 (egal ob Doppelbett oder zwei Einzelbetten / Twin)
//   - MBZ = maxPersons 3+
//
// Zwei Modi (system_settings.room_category_mode):
//   'standard' (DEHOGA): EZ / DZ / MBZ – Doppelzimmer als eigene Kategorie
//   'simple'  (Vereinfacht): EZ / MBZ – kein DZ, Mehrbettzimmer ab 2 Personen
//
// Der Modus steuert:
//   1. getRoomCategoryLabel()   → Badge-Text auf Raumkarten (RoomPropertyBadges)
//   2. getActivePreferenceKeys() → welche Optionen in Dropdowns erscheinen
//   3. Auto-Assign canAdd()     → Präferenz-Matching gegen Raumkapazität
//
// Verwendet von: RoomPropertyBadges, formatUtils (resolveRoomPreferenceConfig,
// ROOM_PREFERENCE_OPTIONS), useAutoAssign, Sidebar, ContentToolbar, EventPlanner
// Kontextmenü, SignuperAdd, DateStats, GanttView.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Zimmerkategorie-Modus.
 *   'standard' – DEHOGA: EZ (1 Person) / DZ (2 Personen) / MBZ (3+ Personen)
 *   'simple'   – Vereinfacht: EZ (1 Person) / MBZ (2+ Personen), kein DZ
 */
export type RoomCategoryMode = 'standard' | 'simple';

/**
 * Fallback-Config pro Preference-DB-Wert (0=Egal, 1=Einzelzimmer, 2=Doppelzimmer, 3=Mehrbettzimmer).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (EZ/DZ/MBZ/Egal).
 *
 * Zimmerkategorie nach DEHOGA = vorgesehene Belegung (maxPersons), nicht Bettenart.
 * Im vereinfachten Modus ('simple') wird Key 2 (DZ) ausgeblendet und wie MBZ behandelt.
 */
export const ROOM_PREFERENCE_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Egal',            shortLabel: 'Egal', color: '#94a3b8', icon: 'bed' },
  1: { label: 'Einzelzimmer',    shortLabel: 'EZ',   color: '#3b82f6', icon: 'bed-single' },
  2: { label: 'Doppelzimmer',    shortLabel: 'DZ',   color: '#f59e0b', icon: 'bed-double' },
  3: { label: 'Mehrbettzimmer',  shortLabel: 'MBZ',  color: '#10b981', icon: 'users' },
};

/**
 * Leitet die Zimmerkategorie (EZ/DZ/MBZ) aus maxPersons und dem Kategorie-Modus ab.
 * DEHOGA: Kategorie = vorgesehene Belegung, nicht Bettenart.
 *
 * @param maxPersons  Maximale Personenzahl des Raums (capacity)
 * @param mode        'standard' (EZ/DZ/MBZ) oder 'simple' (EZ/MBZ)
 * @returns           'EZ' | 'DZ' | 'MBZ' | null (null wenn maxPersons <= 0)
 */
export function getRoomCategoryLabel(maxPersons: number, mode: RoomCategoryMode = 'standard'): string | null {
  if (maxPersons <= 0) return null;
  if (maxPersons === 1) return 'EZ';
  if (mode === 'standard' && maxPersons === 2) return 'DZ';
  return 'MBZ';
}

/**
 * Gibt die aktiven Preference-Keys fuer Dropdowns zurueck (modus-abhaengig).
 * Standard: [0, 1, 2, 3] (Egal/EZ/DZ/MBZ)
 * Vereinfacht: [0, 1, 3] (Egal/EZ/MBZ – kein DZ)
 */
export function getActivePreferenceKeys(mode: RoomCategoryMode = 'standard'): number[] {
  if (mode === 'simple') return [0, 1, 3];
  return [0, 1, 2, 3];
}

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Altersgruppe – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_signupers.age_group TINYINT).
// Werden per deriveColorSet() in Rahmen-, Hintergrund-, Text- und Badge-Farben abgeleitet.
//
// Verwendet von: ItemChip (Badge), Sidebar (Filter), AddSignuperPopup, GuiElements.
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Fallback-Config pro Altersgruppe-DB-Wert (0=Kind, 1=Jugendlich, 2=Erwachsen).
 * Kurzlabel (shortLabel) wird in kompakten Badges angezeigt (K/J/E).
 */
export const AGE_GROUP_DEFAULTS: Record<number, SpecialTypeConfig & { shortLabel: string }> = {
  0: { label: 'Kind',         shortLabel: 'K', color: '#10b981', icon: 'baby' },
  1: { label: 'Jugendlich',   shortLabel: 'J', color: '#f59e0b', icon: 'graduation-cap' },
  2: { label: 'Erwachsen',    shortLabel: 'E', color: '#6366f1', icon: 'user' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// Visuelle Identitaet: Blocktyp (BlockType) – Fallback-Defaults
// ═══════════════════════════════════════════════════════════════════════════════
//
// Keys = DB-Werte (system_dates_timeblocks.block_type, system_roomtypes.default_block_type).
// Werden per deriveColorSet() in Badge-Farben abgeleitet (BlockTypeBadge).
// Icon dient als Fallback wenn der Raumtyp kein eigenes Icon hat.
//
// Verwendet von: RoomPropertyBadges (BlockTypeBadge), TimeBlockCard/TimeBlockListRow
// (Icon-Fallback), constants.tsx (Labels, Select-Options).
// ═══════════════════════════════════════════════════════════════════════════════

import type { BlockType } from '@/types';

/**
 * Fallback-Config pro Blocktyp.
 * Farben orientieren sich am bisherigen Design: grau/violett/amber.
 */
export const BLOCK_TYPE_DEFAULTS: Record<BlockType, SpecialTypeConfig> = {
  mainblock:   { label: 'Hauptblock',     color: '#6b7280', icon: 'course-room' },
  sleepblock:  { label: 'Schlafblock',    color: '#8b5cf6', icon: 'bed' },
  globalblock: { label: 'Globaler Block', color: '#d97706', icon: 'clock' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// SyncBar – Farben für Zeit-Balken (EventPlanner)
// ═══════════════════════════════════════════════════════════════════════════════
//
// Zwei Farbsätze: normal = SyncBar auf Karten, header = SyncBar im Zeitblock-Kopf (embedded).
// Verwendet von: EventPlanner/cards/SyncBar.tsx.
//
// ═══════════════════════════════════════════════════════════════════════════════

export type SyncBarColorSet = {
  /** Hintergrund des Balken-Tracks (grauer Balken). */
  trackBackground: string;
  segmentFallback: string;
  segmentOwnTimes: string;
  segmentException: string;
  markerArrivalDeparture: string;
  gapLine: string;
};

/** Farben für die normale SyncBar (Karten: Person, Preis, Extra). */
export const SYNC_BAR_COLORS: SyncBarColorSet = {
  trackBackground: 'rgba(243, 244, 246, 0)',
  segmentFallback: 'rgb(148, 163, 184)',
  segmentOwnTimes: 'rgb(34, 197, 94)',
  segmentException: 'rgb(239, 68, 68)',
  markerArrivalDeparture: '#000',
  gapLine: '#000',
};

/** Farben für die SyncBar im Zeitblock-Header (embedded). */
export const SYNC_BAR_HEADER_COLORS: SyncBarColorSet = {
  trackBackground: 'rgba(243, 244, 246, 0.8)',
  segmentFallback: 'rgb(148, 163, 184)',
  segmentOwnTimes: 'rgb(34, 197, 94)',
  segmentException: 'rgb(239, 68, 68)',
  markerArrivalDeparture: '#000',
  gapLine: 'rgba(0,0,0,0)',
};
